import { captureException } from '@app/utils/errorReport/errorReport'
import { createLogger } from '@app/utils/loggerCreator'
import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'
import { wrapError } from '@app/utils/wrapError'

import { withAbortSignal } from '@app/packages/abortContext/actions'
import { withDisposableStack } from '@app/packages/disposable/disposable'
import { LazyPromise } from '@app/packages/LazyPromise/LazyPromise'
import { resolveThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { setConfig } from '@app/store/actions/misc'
import { appConfigResultSelector } from '@app/store/slices/app_config'
import { configSelector, setBuildShaSlice } from '@app/store/slices/config'
import { restorePromocodeCookies } from '@app/store/slices/pendingPromocode'
import { fetchRegions, regionsStateSelector } from '@app/store/slices/regions'
import { restoreBannersState } from '@app/store/slices/ui_banners'
import type { Store } from '@app/store/store'
import { createThunk } from '@app/store/thunk'

import { BuildTracker } from './BuildTracker'
import { NetworkStateTracker } from './NetworkStateTracker'

type CheckerLabel = 'profile' | 'regions' | 'debts' | 'card' | 'news' | 'identities' | 'telegram_bot_connection'

export class DataRefreshService {
  logger = createLogger('DataRefresh', 'lightcoral', undefined, IS_BROWSER)
  private store: Store

  private checkers = new Map<CheckerLabel, LazyPromise<any>>()

  onPlaceRestore?: () => Promise<void>

  constructor(store: Store) {
    this.store = store
  }

  async initServer(abortSignal: AbortSignal, location: { pathname: string; search?: string }): Promise<void> {
    await this.store.dispatch(
      withAbortSignal(
        abortSignal,
        createThunk(async dispatch => {
          await Promise.all([
            dispatch(restoreBannersState()),
            dispatch(restorePromocodeCookies(location)),
            dispatch(fetchRegions()),
            // Every render reads app_config — the nav among them — so it is restored here, beside
            // the regions rather than serially behind them, and the readers find it already there.
            dispatch(resolveThunkSelector(appConfigResultSelector)),
          ])
        })
      )
    )
  }

  start() {
    return withDisposableStack(stack => {
      this.logger.log('Start')
      stack.defer(() => {
        this.logger.log('Stop')
      })

      const isApp = configSelector(this.store.getState()).isApp

      if (!isApp) {
        {
          const buildTracker = stack.use(new BuildTracker(this.store, 60 * 1000))
          stack.use(
            buildTracker.sha.listen(sha => {
              if (sha) this.store.dispatch(setBuildShaSlice({ sha }))
            })
          )
        }

        {
          const networkStateTracker = stack.use(new NetworkStateTracker())
          stack.use(
            networkStateTracker.state.sync(state => {
              this.store.dispatch(setConfig({ online: state.online }))
            })
          )
        }
      }

      void this.check()
      stack.adopt(
        setInterval(() => {
          this.check().catch(e => {
            captureException(e, { tags: { service: 'DataRefreshService' } })
          })
        }, 60 * 1000),
        c => clearInterval(c)
      )
    })
  }

  private upsertCheckers<T>(key: CheckerLabel, val: LazyPromise<T>) {
    if (!this.checkers.has(key)) {
      this.checkers.set(key, val)
    }
    return this.checkers.get(key) as LazyPromise<T>
  }

  private isPolygon() {
    return configSelector(this.store.getState()).isPolygon
  }

  private async check() {
    using stack = new DisposableStack()
    if (this.isPolygon()) return

    this.logger.log('Check')
    stack.defer(() => {
      this.logger.log('Check ended')
    })

    try {
      await this.checkRegions()
    } catch (e) {
      throw wrapError(new Error('DataRefresh check failed'), e)
    }
  }

  private async checkRegions() {
    await this.processChecker(
      'regions',
      new LazyPromise(async () => {
        const loadedAt = regionsStateSelector(this.store.getState()).loadedAt
        if (loadedAt && !this.isExpired(moment(loadedAt), this.getExpirationDate(60))) return
        this.logger.log('Check regions')
        await this.store.dispatch(fetchRegions())
      }),
      this.getExpirationDate(60)
    )
  }

  private getExpirationDate(minutes: number) {
    return moment().subtract(minutes, 'minutes')
  }

  private async processChecker<T>(key: CheckerLabel, p: LazyPromise<T>, expireDate: Moment): Promise<T> {
    const checker = this.upsertCheckers(key, p)
    if (checker.loading.value) return checker.getValue()
    if (checker.loadedAt.value && !this.isExpired(checker.loadedAt.value, expireDate)) return checker.getValue()
    checker.unset()
    return await checker.getValue()
  }

  private isExpired(ts: Moment | null, base: Moment) {
    if (!ts) return true
    return ts.isBefore(base)
  }
}
